import React, { useState } from 'react';
import AppLayout from 'layouts/AppLayout';
import { useRole } from 'context/RoleContext';
import { ROLES } from 'config/roleConfig';
import Icon from 'components/AppIcon';
import WorkspaceSection from './components/WorkspaceSection';
import ProfileSection from './components/ProfileSection';
import RolesSection from './components/RolesSection';
import APIKeysSection from './components/APIKeysSection';
import NotificationsSection from './components/NotificationsSection';
import SecuritySection from './components/SecuritySection';
import DemoModeSection from './components/DemoModeSection';

function Toast({ message, onClose }) {
  return (
    <div className="fixed top-5 right-5 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border border-border bg-card z-[9999] max-w-sm animate-in">
      <Icon name="CheckCircle" size={18} color="var(--color-success)" />
      <span className="text-sm text-foreground">{message}</span>
      <button onClick={onClose} className="ml-auto p-0.5 rounded hover:bg-muted">
        <Icon name="X" size={14} color="var(--color-muted-foreground)" />
      </button>
    </div>
  );
}

const NAV_SECTIONS = [
  { id: 'workspace', label: 'Workspace', icon: 'Building2' },
  { id: 'profile', label: 'Profile', icon: 'User' },
  { id: 'roles', label: 'Roles & Permissions', icon: 'ShieldCheck' },
  { id: 'api-keys', label: 'API Keys', icon: 'Key' },
  { id: 'notifications', label: 'Notification Preferences', icon: 'Bell' },
  { id: 'security', label: 'Security', icon: 'Lock' },
  { id: 'demo', label: 'Demo Mode', icon: 'FlaskConical' },
  { id: 'billing', label: 'Billing', icon: 'CreditCard', disabled: true },
];

export default function SettingsPage() {
  const { role } = useRole();
  const [activeSection, setActiveSection] = useState('workspace');
  const [toast, setToast] = useState(null);

  const isAdmin = role === ROLES.PLATFORM_ADMIN;

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'workspace':
        return <WorkspaceSection isAdmin={isAdmin} showToast={showToast} />;
      case 'profile':
        return <ProfileSection showToast={showToast} />;
      case 'roles':
        return <RolesSection showToast={showToast} userRole={role} />;
      case 'api-keys':
        return <APIKeysSection isAdmin={isAdmin} showToast={showToast} />;
      case 'notifications':
        return <NotificationsSection showToast={showToast} />;
      case 'security':
        return <SecuritySection isAdmin={isAdmin} showToast={showToast} />;
      case 'demo':
        return <DemoModeSection showToast={showToast} isAdmin={isAdmin} onNavigateToRoles={() => setActiveSection('roles')} />;
      case 'billing':
        return (
          <div className="bg-card rounded-2xl border border-border shadow-sm p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Icon name="CreditCard" size={28} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Billing — Coming Soon</h3>
            <p className="text-sm text-muted-foreground">Subscription management and invoicing will be available in a future release.</p>
          </div>
        );
      default:
        return null;
    }
  };

  const activeNav = NAV_SECTIONS?.find(s => s?.id === activeSection);

  return (
    <AppLayout>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-in { animation: slideInRight 0.25s ease-out; }
      `}</style>
      <div className="min-h-screen bg-background">
        <div className="px-6 md:px-10 lg:px-12 py-8">

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Settings</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Manage your workspace, preferences, and integrations</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Icon name="Clock" size={13} />
                Last saved: Today at 14:32
              </span>
            </div>
          </div>

          {/* Non-admin banner */}
          {!isAdmin && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 mb-6">
              <Icon name="Info" size={16} color="#d97706" />
              <p className="text-sm text-amber-800 font-medium">Some settings require admin access. Fields marked with <Icon name="Lock" size={12} color="#d97706" className="inline" /> are read-only.</p>
            </div>
          )}

          {/* Dual-panel layout */}
          <div className="flex gap-6 items-start">

            {/* Left Settings Nav */}
            <aside className="w-60 flex-shrink-0 bg-card rounded-2xl border border-border shadow-sm overflow-hidden sticky top-6">
              <div className="px-4 py-3 border-b border-border">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Settings</p>
              </div>
              <nav className="py-2">
                {NAV_SECTIONS?.map(section => {
                  const isActive = activeSection === section?.id;
                  return (
                    <button
                      key={section?.id}
                      onClick={() => !section?.disabled && setActiveSection(section?.id)}
                      disabled={section?.disabled}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all relative ${
                        section?.disabled
                          ? 'text-muted cursor-not-allowed'
                          : isActive
                          ? 'text-primary font-semibold bg-primary/5'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {isActive && (
                        <span className="absolute left-0 top-1 bottom-1 w-0.5 bg-primary rounded-r-full" />
                      )}
                      <Icon
                        name={section?.icon}
                        size={16}
                        color={section?.disabled ? 'var(--color-muted)' : isActive ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                      />
                      <span className="flex-1 text-left">{section?.label}</span>
                      {section?.disabled && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">Soon</span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </aside>

            {/* Main Content Panel */}
            <div className="flex-1 min-w-0">
              {/* Section heading */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={activeNav?.icon || 'Settings'} size={18} color="var(--color-primary)" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-foreground">{activeNav?.label}</h2>
                  <p className="text-xs text-muted-foreground">
                    {activeSection === 'workspace' && 'Configure your workspace settings and branding'}
                    {activeSection === 'profile' && 'Manage your personal profile and account security'}
                    {activeSection === 'roles' && 'Role definitions and demo role switcher'}
                    {activeSection === 'api-keys' && 'Manage API keys and webhook endpoints'}
                    {activeSection === 'notifications' && 'Configure notification delivery and preferences'}
                    {activeSection === 'security' && 'Session management and security controls'}
                    {activeSection === 'demo' && 'Demo environment controls and data management'}
                    {activeSection === 'billing' && 'Subscription and billing management'}
                  </p>
                </div>
              </div>

              {renderSection()}
            </div>
          </div>
        </div>
      </div>
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </AppLayout>
  );
}
